import { AccountConnectionDialog } from '@account/routes/AccountConnections/AccountConnectionDialog';
import { useCreateDropboxTokenMutation } from '@account/services';
import { DropboxFolderType } from '@account/types/dropbox';
import { RepairFiles } from '@assets/icons/RepairFiles';
import MemoryIcon from '@mui/icons-material/Memory';
import { DialogProps, Typography } from '@mui/material';
import { handleError, toaster } from '@utils/common';
import { useState } from 'react';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';
import { GenericActionDialog } from './GenericActionDialog';

const FolderTypeToInfoMap: Record<
    DropboxFolderType,
    { icon: React.ReactNode; title: React.ReactNode; toastName: ReturnType<typeof defineMessage> }
> = {
    [DropboxFolderType.Firmwares]: {
        icon: <MemoryIcon sx={{ fontSize: 30 }} color="primary" />,
        title: <FormattedMessage id="firmwares.disclaimerDialog.title" defaultMessage="Firmware Disclaimer" />,
        toastName: defineMessage({ id: 'account.connections.firmwareFiles.title', defaultMessage: 'Firmware Files' }),
    },
    [DropboxFolderType.Repairs]: {
        icon: <RepairFiles width={30} height={30} fill="#005FBE" />,
        title: <FormattedMessage id="repairs.disclaimerDialog.title" defaultMessage="Repair Files Disclaimer" />,
        toastName: defineMessage({ id: 'account.connections.repairFiles.title', defaultMessage: 'Repair Files' }),
    },
    [DropboxFolderType.Manuals]: {
        icon: <RepairFiles width={30} height={30} fill="#005FBE" />,
        title: <FormattedMessage id="manuals.disclaimerDialog.title" defaultMessage="Manuals Disclaimer" />,
        toastName: defineMessage({ id: 'account.connections.manuals.title', defaultMessage: 'Manuals' }),
    },
};

type Props = Omit<DialogProps, 'title'> & {
    folderType: DropboxFolderType;
};

export const DropboxDisclaimerDialog = ({ folderType, ...props }: Props) => {
    const { title, icon, toastName } = FolderTypeToInfoMap[folderType];
    const [accountConnectionDialogOpen, setAccountConnectionDialogOpen] = useState(false);
    const intl = useIntl();

    const handleDialogClose: NonNullable<DialogProps['onClose']> = (event, reason) => {
        props.onClose?.(event, reason);
    };

    const handleAcceptTerms = () => {
        setAccountConnectionDialogOpen(true);
        handleDialogClose({}, 'backdropClick');
    };

    const handleDeclineTerms = () => {
        handleDialogClose({}, 'backdropClick');
    };

    const [createDropboxToken, { isLoading: isCreatingDropboxToken }] = useCreateDropboxTokenMutation();

    const handleCreateToken = async ({ code }: { code: string }) => {
        try {
            await createDropboxToken({ type: folderType, code }).unwrap();
            toaster.success({
                title: intl.formatMessage(
                    {
                        id: 'account.connections.dropboxTokenCreationSuccess',
                        defaultMessage: '{name} connection created',
                    },
                    {
                        name: intl.formatMessage(toastName),
                    }
                ),
                msg: intl.formatMessage(
                    {
                        id: 'account.connections.dropboxTokenCreationSuccessMessage',
                        defaultMessage: 'The {name} connection has been successfully created.',
                    },
                    {
                        name: intl.formatMessage(toastName),
                    }
                ),
            });
        } catch (err) {
            handleError(
                intl.formatMessage(
                    {
                        id: 'account.connections.dropboxTokenCreationError',
                        defaultMessage: 'An error occurred while creating the {name} connection. Please try again.',
                    },
                    {
                        name: intl.formatMessage(toastName),
                    }
                ),
                err
            );
        }
    };

    return (
        <>
            <GenericActionDialog
                {...props}
                onClose={handleDialogClose}
                keepMounted
                icon={icon}
                title={title}
                content={
                    <>
                        <Typography fontFamily="museo-sans" fontSize={16} fontWeight={600}>
                            <FormattedMessage
                                id="dropbox.disclaimerDialog.importantUsageNotice"
                                defaultMessage="Important Usage Notice"
                            />
                        </Typography>
                        <Typography fontSize={14} color="gray-600" mt={1}>
                            <FormattedMessage
                                id="firmwares.disclaimerDialog.importantUsageNotice.description"
                                defaultMessage="This information and the associated software/firmware are provided <b>for use by trained service professionals</b>. To ensure system safety and reliability, Victron Energy support is optimized for
                                technical experts; as such, we are unable to offer direct guidance to non-certified users regarding these advanced tools."
                                values={{
                                    b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                                }}
                            />
                        </Typography>
                    </>
                }
                buttons={[
                    {
                        label: intl.formatMessage({
                            id: 'dropbox.disclaimerDialog.acceptTerms',
                            defaultMessage: 'Accept Terms',
                        }),
                        buttonProps: {
                            size: 'large',
                            onClick: handleAcceptTerms,
                        },
                    },
                    {
                        label: intl.formatMessage({
                            id: 'dropbox.disclaimerDialog.declineTerms',
                            defaultMessage: 'Decline Terms',
                        }),
                        buttonProps: {
                            size: 'large',
                            onClick: handleDeclineTerms,
                            color: 'error',
                        },
                    },
                ]}
                paperSx={{ borderRadius: '16px' }}
            />
            <AccountConnectionDialog
                open={accountConnectionDialogOpen}
                folderType={folderType}
                scenario="create"
                onCreate={handleCreateToken}
                createLoading={isCreatingDropboxToken}
                onClose={() => {
                    setAccountConnectionDialogOpen(false);
                }}
            />
        </>
    );
};
